import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import classNames from 'classnames';
import isEmail from 'validator/lib/isEmail';
import { publish } from '@/utils/publish';
import { selectValue, toPhoneFormat } from '@/utils/helpers';
import { STATES } from '@/store/models/profile';
import { applyToJobPostingRequest } from '@/store/actions/public.actions';
import { useCloseOnEscape } from '../../utils/useCloseOnEscape';
import styles from './JobPostings.module.scss';

const initialFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  city: '',
  state: ''
};

const initialErrors = {
  firstName: false,
  lastName: false,
  email: false,
  resume: null
};

const RESUME_EXTENSIONS = ['pdf', 'doc', 'docx'];
const RESUME_MAX_SIZE = 10 * 1024 * 1024;

const validateResumeFile = file => {
  if (!file) return null;

  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!RESUME_EXTENSIONS.includes(ext)) return 'Resume must be a PDF, DOC, or DOCX file';
  if (file.size > RESUME_MAX_SIZE) return 'Resume file is too large (max 10MB)';
  return null;
};

const STATE_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: '4.6rem',
    borderRadius: 4,
    boxShadow: 'none',
    borderColor: state.isFocused ? '#3a6f8f' : 'rgba(58, 111, 143, 0.4)',
    borderWidth: state.isFocused ? 2 : 1,
    '&:hover': { borderColor: '#3a6f8f' }
  }),
  valueContainer: base => ({ ...base, padding: '0 1.2rem' }),
  placeholder: base => ({ ...base, color: 'rgba(58, 111, 143, 0.6)', fontSize: '1.6rem' }),
  singleValue: base => ({ ...base, color: '#3a6f8f', fontSize: '1.6rem' }),
  input: base => ({ ...base, color: '#3a6f8f', fontSize: '1.6rem' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: base => ({ ...base, color: '#3a6f8f' }),
  option: (base, state) => ({
    ...base,
    fontSize: '1.6rem',
    backgroundColor: state.isSelected ? '#3a6f8f' : state.isFocused ? 'rgba(58, 111, 143, 0.12)' : '#fff',
    color: state.isSelected ? '#fff' : '#3a6f8f'
  }),
  menuPortal: base => ({ ...base, zIndex: 9999 })
};

export const ApplyModal = ({ job, companyName, webpageUsername, onClose }) => {
  useCloseOnEscape({ isOpen: true, onCloseModal: onClose });

  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState(initialErrors);
  const [resumeFile, setResumeFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const onInputChange = useCallback(e => {
    const { name, value } = e.target;
    setFormData(prevState => ({ ...prevState, [name]: value }));
    setErrors(prevState => ({ ...prevState, [name]: false }));
  }, []);

  const onStateChange = useCallback(option => {
    setFormData(prevState => ({ ...prevState, state: option?.value || '' }));
  }, []);

  const onPhoneBlur = useCallback(e => {
    setFormData(prevState => ({ ...prevState, phone: toPhoneFormat(e.target.value) }));
  }, []);

  const onFileChange = useCallback(e => {
    const file = e.target.files?.[0] || null;
    const error = validateResumeFile(file);
    setResumeFile(error ? null : file);
    setErrors(prevState => ({ ...prevState, resume: error }));
    if (error) e.target.value = '';
  }, []);

  const validate = useCallback(() => {
    const newErrors = {
      firstName: !formData.firstName.trim(),
      lastName: !formData.lastName.trim(),
      email: !formData.email.trim() || !isEmail(formData.email),
      resume: validateResumeFile(resumeFile)
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  }, [formData, resumeFile]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();
      if (!validate()) return;

      const formDataBody = new FormData();
      formDataBody.append('first_name', formData.firstName.trim());
      formDataBody.append('last_name', formData.lastName.trim());
      formDataBody.append('email', formData.email.trim().toLowerCase());
      if (formData.phone) formDataBody.append('phone', formData.phone.trim());
      if (formData.city) formDataBody.append('city', formData.city.trim());
      if (formData.state) formDataBody.append('state', formData.state.trim());
      formDataBody.append('job_title', job.title);
      if (job.id) formDataBody.append('job_posting_id', job.id);
      if (resumeFile) formDataBody.append('resume', resumeFile);

      try {
        setIsSubmitting(true);
        await dispatch(applyToJobPostingRequest({ domain: webpageUsername, formDataBody }));
        setIsSubmitted(true);
      } catch (ex) {
        publish('error', ex);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, resumeFile, job, webpageUsername, validate, dispatch]
  );

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={event => event.stopPropagation()}>
        <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Close apply form">
          ×
        </button>

        {isSubmitted ? (
          <div className={styles.applySuccess}>
            <h3 className={styles.jobDetailTitle}>Application Sent!</h3>
            <p>
              Thanks for applying to {job.title}
              {companyName ? ` at ${companyName}` : ''}. We'll be in touch soon.
            </p>
          </div>
        ) : (
          <>
            <div className={styles.jobDetailHeader}>
              <p className={styles.jobDetailLabel}>Apply Now</p>
              <h3 className={styles.jobDetailTitle}>{job.title}</h3>
            </div>

            <form className={styles.applyForm} onSubmit={onSubmit}>
              <div className={styles.applyRow}>
                <div className={styles.applyField}>
                  <label className={styles.applyFieldLabel} htmlFor="apply-first-name">
                    First Name*
                  </label>
                  <input
                    id="apply-first-name"
                    aria-label="First Name"
                    className={classNames(styles.applyInput, { [styles.inputError]: errors.firstName })}
                    name="firstName"
                    value={formData.firstName}
                    onChange={onInputChange}
                  />
                  {errors.firstName && <p className={styles.applyFieldError}>This field is required</p>}
                </div>
                <div className={styles.applyField}>
                  <label className={styles.applyFieldLabel} htmlFor="apply-last-name">
                    Last Name*
                  </label>
                  <input
                    id="apply-last-name"
                    aria-label="Last Name"
                    className={classNames(styles.applyInput, { [styles.inputError]: errors.lastName })}
                    name="lastName"
                    value={formData.lastName}
                    onChange={onInputChange}
                  />
                  {errors.lastName && <p className={styles.applyFieldError}>This field is required</p>}
                </div>
              </div>

              <div className={styles.applyField}>
                <label className={styles.applyFieldLabel} htmlFor="apply-email">
                  Email Address*
                </label>
                <input
                  id="apply-email"
                  aria-label="Email Address"
                  className={classNames(styles.applyInput, { [styles.inputError]: errors.email })}
                  name="email"
                  value={formData.email}
                  onChange={onInputChange}
                />
                {errors.email && <p className={styles.applyFieldError}>Invalid email address format</p>}
              </div>

              <div className={styles.applyField}>
                <label className={styles.applyFieldLabel} htmlFor="apply-phone">
                  Phone Number
                </label>
                <input
                  id="apply-phone"
                  aria-label="Phone Number"
                  className={styles.applyInput}
                  maxLength={17}
                  name="phone"
                  value={formData.phone}
                  onChange={onInputChange}
                  onBlur={onPhoneBlur}
                />
              </div>

              <div className={styles.applyRow}>
                <div className={styles.applyField}>
                  <label className={styles.applyFieldLabel} htmlFor="apply-city">
                    City
                  </label>
                  <input
                    id="apply-city"
                    aria-label="City"
                    className={styles.applyInput}
                    name="city"
                    value={formData.city}
                    onChange={onInputChange}
                  />
                </div>
                <div className={styles.applyField}>
                  <label className={styles.applyFieldLabel} htmlFor="apply-state">
                    State
                  </label>
                  <Select
                    inputId="apply-state"
                    name="state"
                    options={STATES}
                    value={selectValue(STATES, formData.state)}
                    onChange={onStateChange}
                    styles={STATE_SELECT_STYLES}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                  />
                </div>
              </div>

              <div className={styles.applyField}>
                <label className={styles.applyFieldLabel} htmlFor="apply-resume">
                  Resume
                </label>
                <div className={styles.resumeUpload}>
                  <button
                    type="button"
                    className={styles.resumeUploadButton}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Upload Resume
                  </button>
                  <span className={styles.resumeFileName}>{resumeFile ? resumeFile.name : 'No file chosen'}</span>
                  <input
                    id="apply-resume"
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className={styles.resumeInput}
                    onChange={onFileChange}
                  />
                </div>
                {errors.resume && <p className={styles.applyFieldError}>{errors.resume}</p>}
              </div>

              <button type="submit" className={styles.applySubmitButton} disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
